/**
 * L'assistant natif — le chat du portail, sans iframe (`0015`, P22.4).
 *
 * **Tout se rend depuis l'état.** Le fil est le journal des missions ; ouvrir
 * l'onglet relit ce journal, et le flux SSE ne fait qu'accélérer ce que la
 * relecture sait déjà faire. Un onglet fermé pendant sept minutes de mission
 * rouvre sur la question en attente — réglé par construction, pas par un
 * correctif.
 *
 * **Trois principes de rendu**, et ils ne sont pas cosmétiques :
 *
 * - **une question est un formulaire, pas un message** — les arbitrages
 *   arrivent avec leurs options et leur extrait de plan, on y répond dans le
 *   fil, et la mission repart côté serveur, onglet ouvert ou non ;
 * - **un fil ne se crée qu'au premier message** — un bouton qui insère des
 *   lignes vides en base fabrique la liste de brouillons que personne ne
 *   nomme ni ne relit ;
 * - **le défilement ne vole pas la lecture** — le fil ne suit le bas que si
 *   on y était déjà ; remonter lire un rapport pendant qu'une mission écrit
 *   ne doit pas être un combat.
 *
 * **Le trombone ouvre le navigateur OneDrive** (P22.5) — le même chemin que
 * l'onglet Documents : révisions immuables, instantanés, idempotence.
 */
import {
  useCallback, useEffect, useRef, useState, type ReactNode,
} from "react";

import { api, ErreurApi, type Page } from "../../api/client";
import { Avis } from "../../composants/avis";
import { MOT_MISSION } from "../../composants/etiquettes";
import { Chargement, Echec } from "../../composants/etats";
import { quand } from "../../composants/formats";
import { useDonnees, useRelance } from "../../composants/donnees";
import { NavigateurOneDrive } from "./NavigateurOneDrive";

interface Conversation {
  conversation_id: string;
  creee_le: string;
  premier_message: string;
  derniere_mission: string | null;
  etat: string | null;
  rangement: string | null;
  epinglee: boolean;
}

interface Evenement {
  mission_id?: string;
  seq: number;
  genre: string;
  charge: Record<string, unknown>;
  quand: string;
}

interface PointDArbitrage {
  question?: string;
  local?: string;
  pour_lever?: string;
  pourquoi?: string;
  enjeu?: string;
  options?: string[];
  image?: string;
}

/** Une option de guichet, dépliée.
 *
 * Les agents écrivent `titre :: détail`, avec un préfixe de nature :
 * `=` valeur factuelle, `~` méthode ou hypothèse, `>` report — l'option ne
 * tranche pas, elle renvoie le point. Le titre porte la décision entière :
 * c'est LUI qui part comme réponse et entre au registre ; le détail et le
 * genre n'existent que pour l'œil du métreur. */
interface OptionDuGuichet {
  genre: "" | "valeur" | "methode" | "report";
  titre: string;
  detail: string;
}

const GENRES_DOPTION: Record<string, string> = {
  valeur: "valeur", methode: "méthode", report: "report",
};

function lireOption(brut: string): OptionDuGuichet {
  let texte = brut.trim();
  let genre: OptionDuGuichet["genre"] = "";
  if (texte.startsWith("=")) { genre = "valeur"; texte = texte.slice(1).trim(); }
  else if (texte.startsWith("~")) { genre = "methode"; texte = texte.slice(1).trim(); }
  else if (texte.startsWith(">")) { genre = "report"; texte = texte.slice(1).trim(); }
  const [titre = "", ...suite] = texte.split(" :: ");
  return { genre, titre: titre.trim() || texte, detail: suite.join(" :: ").trim() };
}

/** Les états d'une mission qui n'est pas finie : elle travaille, ou elle
 *  attend qu'un travailleur la prenne. Dans les deux cas l'écran la suit —
 *  une mission en file n'a encore rien produit, et la traiter comme close
 *  la ferait disparaître juste avant qu'elle ne démarre. */
const VIVANTES = new Set(["RUNNING", "QUEUED"]);

/** Les questions d'amorce d'un fil vide. Réelles, pas décoratives : chacune
 *  correspond à une route d'agents qui existe. */
const AMORCES = [
  "Quelle surface de peinture pour la Chambre 4 ?",
  "Que prescrit le CCTP pour les cloisons ?",
  "Identifions les locaux à métrer sur le plan",
];

/** Un rendu de texte de rapport : titres, gras, listes, code en ligne.
 *
 * Les rapports sortent en Markdown léger. Une bibliothèque complète
 * apporterait des tableaux et du HTML qu'aucun rapport ne produit, et une
 * surface d'injection à auditer. Quatre motifs suffisent, et le texte reste
 * du texte. */
function Texte({ brut }: { brut: string }) {
  const enrichir = (texte: string) =>
    texte.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
        return <code key={i} className="mono">{part.slice(1, -1)}</code>;
      }
      return part;
    });

  return (
    <div className="chat__texte">
      {String(brut ?? "").split("\n").map((ligne, i) => {
        const titre = ligne.match(/^#{1,4} (.*)$/);
        if (titre) return <h4 key={i}>{enrichir(titre[1]!)}</h4>;
        if (/^\s*[-•] /.test(ligne)) {
          return (
            <div key={i} className="chat__puce">
              {enrichir(ligne.replace(/^\s*[-•] /, ""))}
            </div>
          );
        }
        if (!ligne.trim()) return <div key={i} className="chat__blanc" />;
        return <p key={i}>{enrichir(ligne)}</p>;
      })}
    </div>
  );
}

/** Le fil en Markdown : questions, arbitrages, décisions, rapports.
 *  Les pas d'agents restent au journal — l'export porte le livrable, pas la
 *  mécanique. */
function filEnMarkdown(evenements: Evenement[], source: Conversation): string {
  const lignes: string[] = [
    `# Conversation de l'assistant — ${quand(source.creee_le)}`,
    "",
  ];
  for (const e of evenements) {
    const charge = e.charge;
    if (e.genre === "message") {
      lignes.push(`## ${String(charge.texte ?? "")}`);
      if (e.quand) lignes.push(`*${quand(e.quand)}*`);
      lignes.push("");
    } else if (e.genre === "depart") {
      const route = (charge.route as string[]) ?? [];
      if (route.length) lignes.push(`_Route : ${route.join(" → ")}_`, "");
    } else if (e.genre === "question") {
      const points = (charge.items as PointDArbitrage[])?.length
        ? (charge.items as PointDArbitrage[])
        : [{
            question: String(charge.question ?? ""),
            options: (charge.options as string[]) ?? [],
          }];
      lignes.push("### Arbitrages demandés", "");
      points.forEach((point, rang) => {
        lignes.push(`${rang + 1}. ${
          point.local ? `**${point.local}** — ` : ""}${point.question ?? ""}`);
        if (point.options?.length) {
          lignes.push(`   Options : ${
            point.options.map((o) => lireOption(o).titre).join(" · ")}`);
        }
      });
      lignes.push("");
    } else if (e.genre === "reponse") {
      if (charge.validation !== undefined) continue;
      const motifs = (charge.motifs as string[]) ?? [];
      const reponses = ((charge.reponses as string[]) ?? [])
        .map((r, i) => (r && motifs[i] ? `${r} *(motif : ${motifs[i]})*` : r))
        .filter(Boolean);
      lignes.push(
        `**Décisions :** ${reponses.join(" · ") || "(sans réponse)"}`, "");
    } else if (e.genre === "rapport") {
      lignes.push(`### Rapport${e.quand ? ` — ${quand(e.quand)}` : ""}`, "",
                  String(charge.texte ?? ""), "");
      const reserves = (charge.reserves as string[]) ?? [];
      if (reserves.length) {
        lignes.push("**Réserves relevées par les outils :**");
        reserves.forEach((reserve) => lignes.push(`- ${reserve}`));
        lignes.push("");
      }
    } else if (e.genre === "echec") {
      lignes.push("### La mission n'a pas abouti", "",
                  String(charge.motif ?? ""), "");
    }
  }
  if (evenements.length === 0) {
    lignes.push(
      "_Ce fil ne porte pas de journal : mission de l'ancien assistant._", "");
  }
  return lignes.filter((l, i, tout) => l !== "" || tout[i - 1] !== "")
    .join("\n");
}

export interface ImageOuverte {
  src: string;
  alt: string;
}

/** Une image du fil : une pièce à examiner, pas une illustration.
 *  Le clic l'agrandit — le bouton rend le geste accessible au clavier. */
function Miniature({
  src, alt, agrandir,
}: ImageOuverte & { agrandir: (image: ImageOuverte) => void }) {
  return (
    <button type="button" className="chat__loupe"
            title="Agrandir l'extrait"
            onClick={() => agrandir({ src, alt })}>
      <img className="chat__image" src={src} alt={alt} />
    </button>
  );
}

/** La visionneuse : un voile, l'image ajustée à l'écran, et deux gestes —
 *  un clic sur l'image passe en taille réelle (la scène défile), un clic à
 *  côté ou Échap referme. « Ouvrir dans un onglet » garde l'extrait sous la
 *  main pendant qu'on répond au guichet. */
export function Visionneuse({
  image, fermer,
}: {
  image: ImageOuverte;
  fermer: () => void;
}) {
  const [pleine, poserPleine] = useState(false);
  const fermerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const rendu = document.activeElement as HTMLElement | null;
    fermerRef.current?.focus();
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === "Escape") fermer();
    };
    window.addEventListener("keydown", surTouche);
    return () => {
      window.removeEventListener("keydown", surTouche);
      rendu?.focus();
    };
  }, [fermer]);

  return (
    <div className="chat__voile" role="dialog" aria-modal="true"
         aria-label={image.alt} onClick={fermer}>
      <div className="chat__voile-tete" onClick={(e) => e.stopPropagation()}>
        <span className="tronque">{image.alt}</span>
        <a className="lien-bouton chat__voile-onglet" href={image.src}
           target="_blank" rel="noreferrer">
          Ouvrir dans un onglet
        </a>
        <button ref={fermerRef} type="button" className="chat__voile-fermer"
                aria-label="Fermer" title="Fermer (Échap)" onClick={fermer}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      </div>
      <div className={`chat__voile-scene${
        pleine ? " chat__voile-scene--pleine" : ""}`}>
        <img src={image.src} alt={image.alt}
             title={pleine ? "Revenir à l'écran" : "Taille réelle"}
             onClick={(e) => {
               e.stopPropagation();
               poserPleine((v) => !v);
             }} />
      </div>
    </div>
  );
}

/** Copier un texte au presse-papier : les chiffres d'un métré, ou une
 *  empreinte de preuve, finissent dans un mail ou un devis, pas dans une
 *  capture d'écran. Le libellé se règle par l'appelant. */
export function Copier({ texte, libelle = "Copier le rapport" }:
                       { texte: string; libelle?: string }) {
  const [fait, poserFait] = useState(false);
  return (
    <button type="button" className="lien-bouton"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(texte);
              } catch {
                // Hors contexte sécurisé (http sur le réseau local), le
                // presse-papier moderne se refuse : le vieux geste reste.
                const zone = document.createElement("textarea");
                zone.value = texte;
                document.body.append(zone);
                zone.select();
                document.execCommand("copy");
                zone.remove();
              }
              poserFait(true);
              window.setTimeout(() => poserFait(false), 2000);
            }}>
      {fait ? "Copié" : libelle}
    </button>
  );
}

export function Chat({ affaireId, amorce, onAmorceConsommee, compact = false }: {
  affaireId: string;
  /** Un message pré-rempli, apporté par une autre vue (ex. « échanger avec les
   *  agents » depuis un local de la reconstruction). Pré-rempli, PAS envoyé :
   *  le métreur le relit et le complète — un fil ne naît qu'au premier envoi. */
  amorce?: string;
  onAmorceConsommee?: () => void;
  /** Mode COMPACT (fenêtre flottante) : la liste des fils latérale est masquée,
   *  la scène occupe toute la largeur. La conversation la plus récente s'ouvre
   *  seule ; « nouvelle conversation » reste accessible depuis l'onglet plein. */
  compact?: boolean;
}) {
  // `null` : rien de choisi — la plus récente s'ouvrira seule. `""` : un
  // fil neuf est voulu — l'accueil reste. Sinon : l'id du fil ouvert.
  const [conversation, poserConversation] = useState<string | null>(null);
  const [fil, poserFil] = useState<Evenement[]>([]);
  const [chargeFil, poserChargeFil] = useState(false);
  const [etatMission, poserEtatMission] = useState<string | null>(null);
  // La question en attente **hors journal** : une mission suspendue par
  // l'ancien assistant n'a rien au journal, sa question vit dans le point de
  // reprise. Le pont qui rend ces missions répondables d'ici.
  const [attente, poserAttente] = useState<{
    mission: string;
    charge: Record<string, unknown>;
  } | null>(null);
  const [missionVive, poserMissionVive] = useState<string>("");
  const [texte, poserTexte] = useState("");
  const [envoi, poserEnvoi] = useState(false);
  const [erreur, poserErreur] = useState("");
  const [depot, poserDepot] = useState(false);
  const [loupe, poserLoupe] = useState<ImageOuverte | null>(null);
  // Le menu d'un fil : lequel, et où — en coordonnées d'écran, car la liste
  // défile et couperait un menu absolu.
  const [menu, poserMenu] = useState<{
    id: string; x: number; y: number;
  } | null>(null);
  const [suppression, poserSuppression] = useState<Conversation | null>(null);
  const [arret, poserArret] = useState<string | null>(null);
  const [voirArchivees, poserVoirArchivees] = useState(false);
  const [avis, poserAvis] = useState("");
  const derouleRef = useRef<HTMLDivElement>(null);
  const champRef = useRef<HTMLTextAreaElement>(null);
  const sseRef = useRef<EventSource | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Le défilement ne suit le bas que si on y était : lire un rapport pendant
  // qu'une mission écrit ne doit pas être un combat contre l'écran.
  const collant = useRef(true);

  const conversations = useDonnees<Page<Conversation>>(
    () => api.lire(`/api/v1/affaires/${affaireId}/conversations`),
    [affaireId],
  );

  // **La colonne des fils se rafraîchit d'elle-même.** Une mission lancée
  // depuis l'atelier de chiffrage, ou reprise par un travailleur, change l'état
  // d'un fil qu'on ne regarde pas ; sans relance, sa pastille restait périmée
  // jusqu'à une action manuelle. La veille s'arrête quand l'onglet est caché.
  useRelance(conversations.recharger, 15000);

  const chargerLeFil = useCallback(async (cible: string, secours = "") => {
    const corps = await api.lire<{ donnees: Evenement[] }>(
      `/api/v1/affaires/${affaireId}/conversations/${cible}/fil`);
    poserFil(corps.donnees);
    poserAttente(null);
    const derniere = corps.donnees.at(-1)?.mission_id ?? secours;
    if (derniere) {
      const etat = await api.lire<{
        donnees: { etat: string | null; attente?: Record<string, unknown> | null };
      }>(
        `/api/v1/affaires/${affaireId}/missions/${derniere}/evenements?depuis=999999`);
      poserEtatMission(etat.donnees.etat);
      poserMissionVive(
        VIVANTES.has(etat.donnees.etat ?? "") ? derniere : "");
      const questionAuJournal = corps.donnees.some(
        (e) => e.mission_id === derniere && e.genre === "question");
      if (etat.donnees.attente && !questionAuJournal) {
        poserAttente({ mission: derniere, charge: etat.donnees.attente });
      }
    } else {
      poserEtatMission(null);
      poserMissionVive("");
    }
  }, [affaireId]);

  // La conversation la plus récente s'ouvre seule : l'onglet doit montrer où
  // l'on en était, pas une page blanche avec un bouton.
  useEffect(() => {
    const premiere = conversations.donnees?.donnees?.[0]?.conversation_id;
    if (premiere && conversation === null) poserConversation(premiere);
  }, [conversations.donnees, conversation]);

  // **Une amorce venue d'ailleurs** (« échanger avec les agents » sur une entité)
  // ouvre un fil NEUF et y pré-remplit le message. On la reconnaît PAR SA VALEUR
  // (et non par un drapeau à sens unique) : dans la fenêtre flottante, ce Chat
  // reste monté et reçoit une amorce PAR entité — un simple booléen n'en
  // consommerait qu'une. Quand l'amorce retombe à vide (consommée par
  // l'appelant), on réarme, pour qu'un nouveau clic — même sur la même entité —
  // ré-amorce. On attend le chargement des fils pour que l'ouverture auto
  // ci-dessus ait déjà tranché.
  const amorceFaite = useRef("");
  useEffect(() => {
    if (!amorce) { amorceFaite.current = ""; return; }
    if (amorceFaite.current === amorce || !conversations.donnees) return;
    amorceFaite.current = amorce;
    nouvelleConversation();
    poserTexte(amorce);
    setTimeout(() => champRef.current?.focus(), 0);
    onAmorceConsommee?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amorce, conversations.donnees]);

  useEffect(() => {
    if (!conversation) return;
    // **On vide avant de charger.** Sans cela, les messages de la conversation
    // précédente restaient sous l'entête de la nouvelle le temps de la
    // requête, et l'accueil « Que voulez-vous chiffrer ? » clignotait. Le
    // fil est mis à blanc et un chargement s'affiche jusqu'à la relecture.
    poserFil([]);
    poserAttente(null);
    poserChargeFil(true);
    const ligne = conversations.donnees?.donnees?.find(
      (c) => c.conversation_id === conversation);
    void chargerLeFil(conversation, ligne?.derniere_mission ?? "")
      .finally(() => poserChargeFil(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation, chargerLeFil]);

  // **Relire d'abord, s'abonner ensuite.** Le SSE part du dernier rang connu ;
  // à sa fermeture (`fin`), on relit l'état — la vérité est côté journal.
  useEffect(() => {
    sseRef.current?.close();
    sseRef.current = null;
    if (!missionVive) return;

    const dernier = fil
      .filter((e) => e.mission_id === missionVive)
      .reduce((max, e) => Math.max(max, e.seq), 0);
    const source = new EventSource(
      `/api/v1/affaires/${affaireId}/missions/${missionVive}/flux?depuis=${dernier}`);
    sseRef.current = source;

    // **La sonde qui débloque un composer coincé.** Si le flux se rompt sans
    // « fin » — serveur tombé, mission tuée en dehors — `missionVive` restait
    // vrai pour toujours et la zone de saisie désarmée : seul un rechargement
    // en sortait. À la coupure, on arme une relecture différée de l'état réel
    // (le journal fait foi) ; s'il n'est plus vivant, le composer se rouvre.
    // Le moindre message reçu prouve que le flux vit et annule la sonde.
    let sonde: ReturnType<typeof setTimeout> | undefined;
    const annulerLaSonde = () => {
      if (sonde) { clearTimeout(sonde); sonde = undefined; }
    };

    source.onmessage = (evenement) => {
      annulerLaSonde();
      const recu = JSON.parse(evenement.data) as Evenement;
      recu.mission_id = missionVive;
      poserFil((avant) =>
        avant.some((e) => e.mission_id === missionVive && e.seq === recu.seq)
          ? avant : [...avant, recu]);
    };
    source.addEventListener("fin", () => {
      annulerLaSonde();
      source.close();
      sseRef.current = null;
      poserMissionVive("");
      if (conversation) void chargerLeFil(conversation);
      conversations.recharger();
    });
    source.onerror = () => {
      // `EventSource` se rouvre seul ; on ne crie pas à une coupure passagère.
      // Mais si elle dure, on relit l'état — sans jamais happer un flux qui
      // reviendrait entre-temps (la relecture est annulée dès le prochain pas).
      if (sonde) return;
      sonde = setTimeout(() => {
        sonde = undefined;
        if (conversation) void chargerLeFil(conversation).catch(() => {});
      }, 8000);
    };
    return () => { annulerLaSonde(); source.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionVive, affaireId]);

  useEffect(() => {
    if (collant.current) {
      derouleRef.current?.scrollTo({ top: derouleRef.current.scrollHeight });
    }
  }, [fil.length, attente]);

  useEffect(() => {
    champRef.current?.focus();
  }, [conversation]);

  useEffect(() => {
    if (!menu) return undefined;
    // **Le menu prend le focus, et les flèches y circulent.** `role="menu"`
    // promet ce comportement ; sans lui, on ouvrait le menu et la tabulation
    // continuait à l'aveugle sous la page. Le focus entre sur le premier geste,
    // les flèches montent et descendent, Échap ferme.
    const items = (): HTMLElement[] => menuRef.current
      ? Array.from(menuRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]'))
      : [];
    items()[0]?.focus();
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === "Escape") { poserMenu(null); return; }
      if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
      const liste = items();
      if (liste.length === 0) return;
      e.preventDefault();
      const i = liste.indexOf(document.activeElement as HTMLElement);
      const j = e.key === "ArrowDown" ? (i < 0 ? 0 : (i + 1) % liste.length)
        : e.key === "ArrowUp" ? (i <= 0 ? liste.length - 1 : i - 1)
        : e.key === "Home" ? 0 : liste.length - 1;
      liste[j]?.focus();
    };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [menu]);

  function surDefilement() {
    const zone = derouleRef.current;
    if (!zone) return;
    collant.current =
      zone.scrollHeight - zone.scrollTop - zone.clientHeight < 80;
  }

  /** Un fil neuf, **sans rien créer** : la ligne naît au premier message.
   *  Un bouton qui insère des brouillons en base fabrique la liste de fils
   *  vides que personne ne nomme ni ne relit. */
  function nouvelleConversation() {
    poserConversation("");
    poserFil([]);
    poserEtatMission(null);
    poserAttente(null);
    poserMissionVive("");
    champRef.current?.focus();
  }

  async function envoyer(contenuForce?: string) {
    const contenu = (contenuForce ?? texte).trim();
    if (!contenu || envoi) return;
    poserErreur("");
    poserEnvoi(true);
    let cible = conversation ?? "";
    try {
      if (!cible) {
        const corps = await api.ecrire<{ donnees: { conversation_id: string } }>(
          `/api/v1/affaires/${affaireId}/conversations`);
        cible = corps.donnees.conversation_id;
        poserConversation(cible);
      }
      const issue = await api.ecrire<{ donnees: { mission_id: string } }>(
        `/api/v1/affaires/${affaireId}/conversations/${cible}/messages`,
        { texte: contenu });
      poserTexte("");
      collant.current = true;
      await chargerLeFil(cible);
      poserMissionVive(issue.donnees.mission_id);
      poserEtatMission("RUNNING");
      conversations.recharger();
    } catch (exc) {
      // Un refus « une mission tourne » prouve que l'écran retardait sur le
      // serveur : on relit l'état — le composer se coupera de lui-même et le
      // flux reprendra. Le texte, lui, reste dans la boîte.
      if (exc instanceof ErreurApi && exc.conflit && cible) {
        void chargerLeFil(cible);
      }
      poserErreur(exc instanceof ErreurApi
        ? `Votre message n'est pas parti — ${exc.message}`
        : "Votre message n'est pas parti — le réseau n'a pas répondu."
          + " Il reste dans la boîte : réessayez.");
    } finally {
      poserEnvoi(false);
    }
  }

  async function arbitrer(mission: string, reponses: string[],
                          motifs?: string[]): Promise<boolean> {
    poserErreur("");
    try {
      await api.ecrire(
        `/api/v1/affaires/${affaireId}/missions/${mission}/arbitrages`,
        { reponses, ...(motifs ? { motifs } : {}) });
      collant.current = true;
      if (conversation) await chargerLeFil(conversation, mission);
      poserMissionVive(mission);
      poserEtatMission("RUNNING");
      return true;
    } catch (exc) {
      poserErreur(exc instanceof ErreurApi ? exc.message
                  : "Les décisions ne sont pas parties.");
      return false;
    }
  }

  async function ranger(
    cible: string, corps: { epinglee?: boolean; rangement?: string },
  ) {
    poserMenu(null);
    poserErreur("");
    try {
      await api.modifier(
        `/api/v1/affaires/${affaireId}/conversations/${cible}`, corps);
      conversations.recharger();
    } catch (exc) {
      poserErreur(exc instanceof ErreurApi ? exc.message
                  : "Le rangement n'a pas abouti.");
    }
  }

  async function arreterLaMission(mission: string) {
    poserErreur("");
    try {
      await api.ecrire(
        `/api/v1/affaires/${affaireId}/missions/${mission}/arret`);
      poserArret(null);
      if (conversation) await chargerLeFil(conversation);
      conversations.recharger();
    } catch (exc) {
      poserArret(null);
      poserErreur(exc instanceof ErreurApi ? exc.message
                  : "L'arrêt n'a pas abouti.");
    }
  }

  async function supprimerLeFil(cible: Conversation) {
    poserErreur("");
    try {
      await api.retirer(
        `/api/v1/affaires/${affaireId}/conversations/${cible.conversation_id}`);
      poserSuppression(null);
      if (conversation === cible.conversation_id) nouvelleConversation();
      conversations.recharger();
    } catch (exc) {
      poserSuppression(null);
      poserErreur(exc instanceof ErreurApi ? exc.message
                  : "La suppression n'a pas abouti.");
    }
  }

  async function exporter(cible: Conversation) {
    poserMenu(null);
    poserErreur("");
    try {
      const corps = await api.lire<{ donnees: Evenement[] }>(
        `/api/v1/affaires/${affaireId}/conversations/${cible.conversation_id}/fil`);
      const lien = document.createElement("a");
      lien.href = URL.createObjectURL(new Blob(
        [filEnMarkdown(corps.donnees, cible)],
        { type: "text/markdown;charset=utf-8" }));
      lien.download = `assistant-${cible.conversation_id.slice(0, 8)}.md`;
      lien.click();
      URL.revokeObjectURL(lien.href);
    } catch (exc) {
      poserErreur(exc instanceof ErreurApi ? exc.message
                  : "L'export n'a pas abouti.");
    }
  }

  async function valider(mission: string, accord: boolean): Promise<boolean> {
    poserErreur("");
    try {
      await api.ecrire(
        `/api/v1/affaires/${affaireId}/missions/${mission}/validation`,
        { accord });
      return true;
    } catch (exc) {
      poserErreur(exc instanceof ErreurApi ? exc.message
                  : "La décision n'est pas partie.");
      return false;
    }
  }

  if (conversations.charge && !conversations.donnees) {
    return <Chargement lignes={3} />;
  }
  if (conversations.erreur) {
    return <Echec erreur={conversations.erreur}
                  reessayer={conversations.recharger} />;
  }

  const liste = conversations.donnees?.donnees ?? [];
  const ouvertes = liste.filter((c) => c.rangement !== "ARCHIVED");
  const archivees = liste.filter((c) => c.rangement === "ARCHIVED");
  const filArchive = liste.find(
    (c) => c.conversation_id === conversation)?.rangement === "ARCHIVED";
  const enCours = VIVANTES.has(etatMission ?? "");
  const filVide = fil.length === 0 && !attente;

  const ligneDeFil = (c: Conversation) => (
    <div
      key={c.conversation_id}
      className={`chat__fil${
        c.conversation_id === conversation ? " chat__fil--ouvert" : ""}${
        c.rangement === "ARCHIVED" ? " chat__fil--archivee" : ""}`}
    >
      <button type="button" className="chat__fil-corps"
              onClick={() => poserConversation(c.conversation_id)}>
        <span className="tronque">
          {c.epinglee && (
            <svg className="chat__epingle" width="11" height="11"
                 viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 22s-7-6.2-7-12a7 7 0 1 1 14 0c0 5.8-7 12-7 12Z" />
              <circle cx="12" cy="10" r="2.6" fill="var(--mo-surface)" />
            </svg>
          )}
          {c.premier_message || "Mission de l'ancien assistant"}
        </span>
        <span className="chat__fil-meta">
          {quand(c.creee_le)}
          {c.etat === "SUSPENDED" && (
            <span className="chat__attente">{MOT_MISSION.SUSPENDED}</span>
          )}
          {c.etat === "RUNNING" && (
            <span className="chat__vif">{MOT_MISSION.RUNNING}</span>
          )}
          {c.etat === "QUEUED" && (
            <span className="chat__vif">{MOT_MISSION.QUEUED}</span>
          )}
        </span>
      </button>
      <button
        type="button"
        className="action chat__fil-gestes"
        aria-label="Gérer la conversation"
        title="Gérer la conversation"
        aria-haspopup="menu"
        aria-expanded={menu?.id === c.conversation_id}
        onClick={(e) => {
          const cadre = e.currentTarget.getBoundingClientRect();
          poserMenu(menu?.id === c.conversation_id ? null
                    : { id: c.conversation_id,
                        x: cadre.right, y: cadre.bottom + 4 });
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"
             aria-hidden="true">
          <circle cx="5" cy="12" r="1.8" />
          <circle cx="12" cy="12" r="1.8" />
          <circle cx="19" cy="12" r="1.8" />
        </svg>
      </button>
    </div>
  );

  return (
    <div className={"chat" + (compact ? " chat--compact" : "")}>
      {!compact && (
        <aside className="chat__fils">
          <button type="button" className="bouton"
                  onClick={nouvelleConversation}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Nouvelle conversation
          </button>
          <div className="chat__liste">
            {ouvertes.map(ligneDeFil)}
            {liste.length === 0 && (
              <span className="discret">
                Vos conversations avec l'assistant apparaîtront ici.
              </span>
            )}
            {archivees.length > 0 && (
              <>
                <button type="button" className="chat__archives"
                        aria-expanded={voirArchivees}
                        onClick={() => poserVoirArchivees((v) => !v)}>
                  {voirArchivees ? "Masquer les archivées"
                    : `Archivées (${archivees.length})`}
                </button>
                {voirArchivees && archivees.map(ligneDeFil)}
              </>
            )}
          </div>
        </aside>
      )}

      <section className="chat__scene">
        <div className="chat__deroule" ref={derouleRef}
             onScroll={surDefilement}>
          {chargeFil && filVide ? (
            <div className="chat__accueil">
              <Chargement lignes={3} />
            </div>
          ) : filVide && !enCours ? (
            <div className="chat__accueil">
              <h3>Que voulez-vous chiffrer ?</h3>
              <p className="discret">
                La mission observera l'instantané actif de l'affaire, et chaque
                nombre rendu remontera à son calque, son article ou son calcul.
              </p>
              <div className="chat__amorces">
                {AMORCES.map((amorce) => (
                  <button key={amorce} type="button" className="chat__amorce"
                          onClick={() => void envoyer(amorce)}>
                    {amorce}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <Deroule
              affaireId={affaireId}
              fil={fil}
              etatMission={etatMission}
              arbitrer={arbitrer}
              valider={valider}
              agrandir={poserLoupe}
              demanderLArret={() => missionVive && poserArret(missionVive)}
            />
          )}
          {attente && etatMission === "SUSPENDED" && (
            <Guichet
              evenement={{ seq: 0, genre: "question", quand: "",
                           mission_id: attente.mission,
                           charge: attente.charge }}
              actif
              repondre={(reponses, motifs) =>
                arbitrer(attente.mission, reponses, motifs)}
              agrandir={poserLoupe}
            />
          )}
        </div>

        {filArchive && (
          <div className="chat__bandeau">
            Fil archivé — il se consulte ; rouvrez-le pour continuer.
            <button type="button" className="lien-bouton"
                    onClick={() => conversation
                      && void ranger(conversation, { rangement: "OPEN" })}>
              Rouvrir
            </button>
          </div>
        )}
        {erreur && (
          <div className="chat__bandeau chat__bandeau--erreur" role="alert">
            <span>{erreur}</span>
            <button type="button" className="lien-bouton"
                    onClick={() => poserErreur("")}>
              Fermer
            </button>
          </div>
        )}
        {avis && <Avis ton="info" cle={avis}>{avis}</Avis>}

        <div className="chat__composer">
          <button
            type="button"
            className="action"
            title="Ajouter des pièces depuis OneDrive"
            aria-label="Ajouter des pièces depuis OneDrive"
            onClick={() => poserDepot(true)}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="m21 12-8.5 8.5a5 5 0 0 1-7-7L14 5a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L16 7" />
            </svg>
          </button>
          <textarea
            ref={champRef}
            value={texte}
            rows={1}
            placeholder={enCours
              ? "Une mission tourne — sa réponse arrivera ici."
              : filArchive
                ? "Ce fil est archivé — rouvrez-le pour écrire."
                : "Votre question — Entrée envoie, Maj+Entrée passe à la ligne"}
            disabled={enCours || filArchive}
            aria-label="Votre question à l'assistant"
            onChange={(e) => poserTexte(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void envoyer();
              }
            }}
          />
          <button
            type="button"
            className="bouton bouton--primaire chat__envoyer"
            aria-label="Envoyer"
            title="Envoyer (Entrée)"
            disabled={enCours || filArchive || envoi || !texte.trim()}
            onClick={() => void envoyer()}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
              <path d="M12 19V5M5 12l7-7 7 7" />
            </svg>
          </button>
        </div>
      </section>

      {menu && (() => {
        const cible = liste.find((c) => c.conversation_id === menu.id);
        if (!cible) return null;
        const rangee = cible.rangement === "ARCHIVED";
        return (
          <>
            <div className="chat__menu-fond" onClick={() => poserMenu(null)} />
            <div ref={menuRef} className="chat__menu" role="menu"
                 aria-label="Gestes sur la conversation"
                 style={{ top: menu.y, left: menu.x }}>
              {!rangee && (
                <button type="button" role="menuitem"
                        onClick={() => void ranger(cible.conversation_id,
                                                   { epinglee: !cible.epinglee })}>
                  {cible.epinglee ? "Désépingler" : "Épingler en tête"}
                </button>
              )}
              <button type="button" role="menuitem"
                      onClick={() => void ranger(cible.conversation_id,
                        { rangement: rangee ? "OPEN" : "ARCHIVED" })}>
                {rangee ? "Rouvrir" : "Archiver"}
              </button>
              <button type="button" role="menuitem"
                      onClick={() => void exporter(cible)}>
                Exporter en Markdown
              </button>
              {(VIVANTES.has(cible.etat ?? "") || cible.etat === "SUSPENDED")
                && cible.derniere_mission && (
                <button type="button" role="menuitem"
                        className="chat__menu-danger"
                        onClick={() => {
                          poserMenu(null);
                          poserArret(cible.derniere_mission);
                        }}>
                  Arrêter la mission…
                </button>
              )}
              <button type="button" role="menuitem" className="chat__menu-danger"
                      onClick={() => {
                        poserMenu(null);
                        poserSuppression(cible);
                      }}>
                Supprimer…
              </button>
            </div>
          </>
        );
      })()}

      {arret && (
        <div className="voile" role="dialog" aria-modal="true"
             aria-label="Arrêter la mission">
          <div className="modale" style={{ width: "min(480px, 100%)" }}>
            <div className="modale__tete"><h2>Arrêter la mission ?</h2></div>
            <div className="modale__corps"
                 style={{ padding: "var(--mo-space-5)" }}>
              <div className="pile">
                <div className="avis avis--attention">
                  Elle s'arrêtera où elle en est. Le journal garde les pas
                  faits, et vos décisions restent acquises au registre — une
                  prochaine mission ne les redemandera pas.
                </div>
              </div>
            </div>
            <div className="modale__pied">
              <span style={{ flex: 1 }} />
              <button type="button" className="bouton"
                      onClick={() => poserArret(null)}>
                Annuler
              </button>
              <button type="button" className="bouton bouton--danger"
                      onClick={() => void arreterLaMission(arret)}>
                Arrêter maintenant
              </button>
            </div>
          </div>
        </div>
      )}

      {suppression && (
        <div className="voile" role="dialog" aria-modal="true"
             aria-label="Supprimer la conversation">
          <div className="modale" style={{ width: "min(480px, 100%)" }}>
            <div className="modale__tete">
              <h2>Supprimer cette conversation ?</h2>
            </div>
            <div className="modale__corps"
                 style={{ padding: "var(--mo-space-5)" }}>
              <div className="pile">
                <p>
                  <strong>
                    {suppression.premier_message
                      || "Mission de l'ancien assistant"}
                  </strong>
                </p>
                <div className="avis avis--attention">
                  Le fil, ses missions et leur journal s'effacent — sans
                  retour arrière. Les métrés déjà produits restent dans
                  l'affaire.
                </div>
                {suppression.etat === "SUSPENDED" && (
                  <div className="avis avis--attention">
                    Ce fil attend vos décisions : les questions disparaîtront
                    avec lui.
                  </div>
                )}
              </div>
            </div>
            <div className="modale__pied">
              <span style={{ flex: 1 }} />
              <button type="button" className="bouton"
                      onClick={() => poserSuppression(null)}>
                Annuler
              </button>
              <button type="button" className="bouton bouton--danger"
                      onClick={() => void supprimerLeFil(suppression)}>
                Supprimer la conversation
              </button>
            </div>
          </div>
        </div>
      )}

      {loupe && <Visionneuse image={loupe} fermer={() => poserLoupe(null)} />}

      {depot && (
        <NavigateurOneDrive
          affaireId={affaireId}
          categorieInitiale="CCTP"
          fermer={() => poserDepot(false)}
          importe={() =>
            poserAvis(
              "Pièces importées. Elles entreront au prochain instantané — "
              + "préparez-le puis activez-le pour que les missions les lisent.",
            )
          }
        />
      )}
    </div>
  );
}

/** Le fil rendu, groupé par mission.
 *
 * Chaque mission est un bloc : le message qui l'a lancée, sa route, ses pas
 * sous un rail discret, son issue. Les pas se replient par paire
 * (`repond_a`) ; un agent hors route se tait — le journal dit tout, l'écran
 * choisit quoi montrer. */
function Deroule({
  affaireId, fil, etatMission, arbitrer, valider, agrandir, demanderLArret,
}: {
  affaireId: string;
  fil: Evenement[];
  etatMission: string | null;
  arbitrer: (mission: string, reponses: string[], motifs?: string[]) => Promise<boolean>;
  valider: (mission: string, accord: boolean) => Promise<boolean>;
  agrandir: (image: ImageOuverte) => void;
  demanderLArret: () => void;
}) {
  const fins = new Map<string, Evenement>();
  for (const e of fil) {
    if (e.genre === "pas" && e.charge.etat === "fin") {
      fins.set(`${e.mission_id}:${e.charge.repond_a}`, e);
    }
  }
  const derniereMission = fil.at(-1)?.mission_id;

  const missions: { id: string; evenements: Evenement[] }[] = [];
  for (const e of fil) {
    const bloc = missions.at(-1);
    if (bloc && bloc.id === e.mission_id) bloc.evenements.push(e);
    else missions.push({ id: e.mission_id ?? "", evenements: [e] });
  }

  return (
    <>
      {missions.map((bloc) => (
        <MissionBloc
          key={bloc.id}
          affaireId={affaireId}
          evenements={bloc.evenements}
          fins={fins}
          etat={bloc.id === derniereMission ? etatMission : null}
          arbitrer={(reponses, motifs) => arbitrer(bloc.id, reponses, motifs)}
          valider={(accord) => valider(bloc.id, accord)}
          agrandir={agrandir}
        />
      ))}
      {VIVANTES.has(etatMission ?? "") && (
        <div className="chat__vivant" aria-live="polite">
          <span className="chat__pouls" aria-hidden="true" />
          {etatMission === "QUEUED"
            ? "La mission attend qu'un travailleur la prenne — cette page peut se fermer."
            : "La mission travaille — cette page peut se fermer, rien ne sera perdu."}
          <button type="button" className="lien-bouton chat__arret"
                  onClick={demanderLArret}>
            Arrêter la mission
          </button>
        </div>
      )}
      {/* **La suspension s'annonce.** Le bandeau « travaille » disparaît au
          passage SUSPENDED — l'instant précis où la présence du métreur est
          requise — et le guichet apparaissait muet pour un lecteur d'écran.
          Ce `role="status"` le dit, et donne au regard un repère de plus. */}
      {etatMission === "SUSPENDED" && (
        <div className="chat__vivant chat__vivant--attente" role="status">
          <span className="chat__pouls chat__pouls--attente" aria-hidden="true" />
          La mission attend vos arbitrages — répondez au guichet ci-dessus pour
          qu'elle reprenne.
        </div>
      )}
    </>
  );
}

function MissionBloc({
  affaireId, evenements, fins, etat, arbitrer, valider, agrandir,
}: {
  affaireId: string;
  evenements: Evenement[];
  fins: Map<string, Evenement>;
  etat: string | null;
  arbitrer: (reponses: string[], motifs?: string[]) => Promise<boolean>;
  valider: (accord: boolean) => Promise<boolean>;
  agrandir: (image: ImageOuverte) => void;
}) {
  const mission = evenements[0]?.mission_id ?? "";
  const visibles = new Set(
    evenements
      .filter((e) => e.genre === "pas" && e.charge.etat === "debut")
      .filter((e) => {
        const fin = fins.get(`${mission}:${e.seq}`);
        if (e.charge.genre === "agent" && fin?.charge.vide) return false;
        if (e.charge.genre === "reflexion" && fin) return false;
        return true;
      })
      .map((e) => e.seq),
  );

  // Les pas consécutifs partagent **un seul** rail : l'aparté des agents se
  // lit d'un trait, pas en pointillés au rythme des interlignes.
  const rendus: ReactNode[] = [];
  let rail: ReactNode[] = [];
  const viderLeRail = () => {
    if (rail.length) {
      rendus.push(
        <div key={`rail-${rendus.length}`} className="chat__rail">
          {rail}
        </div>,
      );
      rail = [];
    }
  };

  for (const e of evenements) {
    const cle = `${e.mission_id}:${e.seq}`;

    if (e.genre === "pas") {
      if (e.charge.etat === "debut" && visibles.has(e.seq)) {
        rail.push(
          <Pas key={cle} affaireId={affaireId} mission={mission} debut={e}
               fin={fins.get(cle)} agrandir={agrandir} />,
        );
      }
      continue;
    }
    viderLeRail();

    const element = (() => {
      if (e.genre === "message") {
          return (
            <div key={cle} className="chat__bulle chat__bulle--moi"
                 title={quand(e.quand)}>
              {String(e.charge.texte ?? "")}
            </div>
          );
        }
        if (e.genre === "depart") {
          const route = (e.charge.route as string[]) ?? [];
          return route.length ? (
            <div key={cle} className="chat__route"
                 aria-label="Route de la mission">
              {route.map((role, rang) => (
                <span key={role} className="chat__etape-route">
                  {rang > 0 && <span aria-hidden="true">→</span>}
                  <span className="chat__role">{role}</span>
                </span>
              ))}
            </div>
          ) : null;
        }
        if (e.genre === "question") {
          return (
            <Guichet key={cle} evenement={e}
                     actif={etat === "SUSPENDED"}
                     repondre={arbitrer}
                     agrandir={agrandir} />
          );
        }
        if (e.genre === "reponse") {
          if (e.charge.validation !== undefined) return null;
          const motifs = (e.charge.motifs as string[]) ?? [];
          const reponses = ((e.charge.reponses as string[]) ?? [])
            .map((r, i) => (r && motifs[i] ? `${r} — motif : ${motifs[i]}` : r))
            .filter(Boolean);
          return (
            <div key={cle} className="chat__bulle chat__bulle--moi">
              {reponses.join(" · ") || "(sans réponse)"}
            </div>
          );
        }
        if (e.genre === "validation") {
          return (
            <GuichetClasseur key={cle}
              demande={String(e.charge.demande ?? "")}
              actif={etat === "RUNNING"} valider={valider} />
          );
        }
        if (e.genre === "rapport") {
          const duree = Number(e.charge.duree_s ?? 0);
          const jetons = (e.charge.jetons as { entree?: number; sortie?: number }) ?? {};
          const reserves = (e.charge.reserves as string[]) ?? [];
          return (
            <div key={cle} className="chat__rapport">
              <Texte brut={String(e.charge.texte ?? "")} />
              {reserves.length > 0 && (
                <div className="chat__reserves">
                  <strong>Réserves relevées par les outils</strong>
                  {reserves.map((r) => (
                    <div key={r} className="chat__puce">{r}</div>
                  ))}
                </div>
              )}
              <div className="chat__pied">
                <span>{quand(e.quand)}</span>
                {duree > 0 && <span>{duree} s</span>}
                {jetons.entree ? (
                  <span>{jetons.entree} + {jetons.sortie} jetons</span>
                ) : null}
                <Copier texte={String(e.charge.texte ?? "")} />
              </div>
            </div>
          );
        }
        if (e.genre === "echec") {
          return (
            <div key={cle} className="chat__guichet chat__guichet--echec">
              <strong>La mission n'a pas abouti.</strong>
              <Texte brut={String(e.charge.motif ?? "")} />
            </div>
          );
        }
      return null;
    })();
    if (element) rendus.push(element);
  }
  viderLeRail();

  return <div className="chat__mission">{rendus}</div>;
}

function Pas({
  affaireId, mission, debut, fin, agrandir,
}: {
  affaireId: string;
  mission: string;
  debut: Evenement;
  fin?: Evenement;
  agrandir: (image: ImageOuverte) => void;
}) {
  const nom = String(debut.charge.nom ?? "");
  const entree = String(debut.charge.entree ?? "");
  const sortie = String(fin?.charge.sortie ?? "");
  const images = (fin?.charge.images as string[]) ?? [];
  const genre = String(debut.charge.genre ?? "");

  if (genre === "reflexion") {
    return (
      <div className="chat__pas chat__pas--pense">
        <span className="chat__pouls" aria-hidden="true" />
        {nom}…
      </div>
    );
  }
  if (genre === "agent") {
    return (
      <div className="chat__pas chat__pas--agent">
        <span className="chat__jalon" aria-hidden="true" />
        {nom}
        {!fin && <span className="chat__pouls" aria-hidden="true" />}
      </div>
    );
  }
  return (
    <details className="chat__pas chat__pas--outil">
      <summary>
        <svg className="chat__chevron" width="12" height="12"
             viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2.4" aria-hidden="true">
          <path d="m9 6 6 6-6 6" />
        </svg>
        <span className="mono">{nom}</span>
        {entree && <span className="discret tronque">{entree}</span>}
        {!fin && <span className="chat__pouls" aria-hidden="true" />}
      </summary>
      {sortie && <pre className="chat__sortie">{sortie}</pre>}
      {images.map((chemin) => (
        <Miniature
          key={chemin}
          alt={`Extrait de plan — ${nom}`}
          src={`/api/v1/affaires/${affaireId}/missions/${mission}/images/${chemin.replace(/^pas\//, "")}`}
          agrandir={agrandir}
        />
      ))}
    </details>
  );
}

/** Le guichet d'arbitrage — la raison d'être de la décision `0015`.
 *
 * L'extrait de plan se montre **avant** la question : le métreur doit l'avoir
 * sous les yeux au moment de choisir, pas après avoir cliqué. Les options
 * n'excluent pas la réponse libre — la bonne réponse est parfois celle que
 * personne n'a prévue. Le formulaire reste dans l'historique une fois
 * répondu, grisé, comme une pièce du dossier. */
/** L'accord d'écriture au classeur — un geste à conséquence, transmis une
 *  seule fois. Le bouton se désarme pendant l'appel : un double-clic n'envoyait
 *  pas deux décisions par hasard sur le classeur du client. */
function GuichetClasseur({ demande, actif, valider }: {
  demande: string;
  actif: boolean;
  valider: (accord: boolean) => Promise<boolean>;
}) {
  const [soumis, poserSoumis] = useState(false);
  const trancher = async (accord: boolean) => {
    if (soumis) return;
    poserSoumis(true);
    try { await valider(accord); } finally { poserSoumis(false); }
  };
  return (
    <div className="chat__guichet">
      <strong>Écrire dans le classeur du client ?</strong>
      <Texte brut={demande} />
      {actif && (
        <div className="rangee">
          <button type="button" className="bouton bouton--primaire"
                  disabled={soumis} onClick={() => void trancher(true)}>
            {soumis ? "Enregistrement…" : "Enregistrer"}
          </button>
          <button type="button" className="bouton"
                  disabled={soumis} onClick={() => void trancher(false)}>
            Ne pas enregistrer
          </button>
        </div>
      )}
    </div>
  );
}

export function Guichet({
  evenement, actif, repondre, agrandir,
}: {
  evenement: Evenement;
  actif: boolean;
  repondre: (reponses: string[], motifs?: string[]) => Promise<boolean>;
  agrandir: (image: ImageOuverte) => void;
}) {
  const points = (evenement.charge.items as PointDArbitrage[])?.length
    ? (evenement.charge.items as PointDArbitrage[])
    : [{
        question: String(evenement.charge.question ?? ""),
        options: (evenement.charge.options as string[]) ?? [],
        pourquoi: String(evenement.charge.pourquoi ?? ""),
        enjeu: String(evenement.charge.enjeu ?? ""),
        image: String(evenement.charge.image ?? ""),
      }];
  const [reponses, poserReponses] = useState<string[]>(
    () => points.map(() => ""));
  const [motifs, poserMotifs] = useState<string[]>(
    () => points.map(() => ""));
  const [libres, poserLibres] = useState<boolean[]>(
    () => points.map(() => false));
  const [envoye, poserEnvoye] = useState(false);
  const [soumis, poserSoumis] = useState(false);

  // **`envoye` ne se pose qu'au succès.** Le posé d'abord, l'appel ensuite,
  // laissait « Décisions transmises » à l'écran quand l'envoi échouait — le
  // métreur croyait sa décision passée alors qu'elle était perdue. On attend
  // l'issue, et le bouton reste désarmé pendant l'appel : pas de double envoi.
  const transmettre = async () => {
    if (soumis) return;
    poserSoumis(true);
    try {
      const ok = await repondre(
        reponses, motifs.some((m) => m.trim()) ? motifs : undefined);
      if (ok) poserEnvoye(true);
    } finally {
      poserSoumis(false);
    }
  };

  const intro = String(evenement.charge.intro ?? "");
  const tranchees = reponses.filter((r) => r.trim()).length;

  const poser = (rang: number, valeur: string) =>
    poserReponses((avant) => avant.map((r, i) => (i === rang ? valeur : r)));

  return (
    <div className={`chat__guichet${actif ? "" : " chat__guichet--passe"}`}>
      <div className="chat__guichet-tete">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M12 9v4M12 17h.01" />
          <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        </svg>
        <strong>Un agent a besoin de vous</strong>
        {points.length > 1 && (
          <span className="chat__guichet-compte">
            {points.length} points à trancher
          </span>
        )}
      </div>
      {intro && <p className="chat__guichet-intro">{intro}</p>}

      {points.map((point, rang) => {
        const contexte = point.pour_lever ?? point.pourquoi ?? "";
        const options = (point.options ?? []).map(lireOption);
        const libre = libres[rang] || options.length === 0;
        // Des valeurs courtes se comparent en rangée ; une réponse longue,
        // ou détaillée, se lit en liste.
        const enListe = options.some((o) => o.detail || o.titre.length > 40);
        // **Le tabindex roving du radiogroup.** Une seule carte est tabulable
        // — la choisie, ou la première à défaut ; les flèches font le reste.
        // C'est ce qu'un `role="radiogroup"` promet, et qu'il ne tenait pas.
        const rangChoisi = libres[rang]
          ? options.length
          : options.findIndex((o) => o.titre === reponses[rang]);
        const rangTabbable = rangChoisi < 0 ? 0 : rangChoisi;
        const flecher = (evenement: React.KeyboardEvent<HTMLDivElement>) => {
          if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"]
                .includes(evenement.key)) return;
          evenement.preventDefault();
          const radios = Array.from(
            evenement.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]'));
          const i = radios.indexOf(document.activeElement as HTMLElement);
          const suivant = evenement.key === "ArrowDown"
            || evenement.key === "ArrowRight";
          const j = i < 0 ? 0
            : (i + (suivant ? 1 : -1) + radios.length) % radios.length;
          radios[j]?.focus();
          radios[j]?.click();  // sélectionne, en réutilisant l'onClick existant
        };
        return (
          <div key={rang} className="chat__point">
            {points.length > 1 && (
              <span className="chat__point-rang" aria-hidden="true">
                {rang + 1}
              </span>
            )}
            <div className="chat__point-corps">
              {point.image && (
                <Miniature src={point.image}
                           alt="Extrait de plan joint à la question"
                           agrandir={agrandir} />
              )}
              <div className="chat__point-question">
                {point.local && <strong>{point.local} — </strong>}
                {point.question}
              </div>
              {point.enjeu && (
                <div className="chat__point-enjeu">
                  <strong>Enjeu :</strong> {point.enjeu}
                </div>
              )}
              {contexte && contexte !== point.question && (
                <div className="chat__point-pourquoi">
                  <span className="chat__point-pourquoi-titre">
                    Pourquoi l'agent demande
                  </span>
                  <div className="chat__point-contexte">
                    <Texte brut={contexte} />
                  </div>
                </div>
              )}
              {actif && !envoye && (
                <>
                  {options.length > 0 && (
                    <div className={`chat__cartes${
                           enListe ? "" : " chat__cartes--rangee"}`}
                         role="radiogroup"
                         aria-label={`Options du point ${rang + 1}`}
                         onKeyDown={flecher}>
                      {options.map((option, o) => {
                        const choisie = !libres[rang]
                          && reponses[rang] === option.titre;
                        return (
                          <button
                            key={`${o}-${option.titre}`}
                            type="button"
                            role="radio"
                            aria-checked={choisie}
                            tabIndex={o === rangTabbable ? 0 : -1}
                            className={`chat__carte${
                              choisie ? " chat__carte--choisie" : ""}${
                              option.genre === "report"
                                ? " chat__carte--report" : ""}`}
                            onClick={() => {
                              poserLibres((avant) => avant.map(
                                (l, i) => (i === rang ? false : l)));
                              poser(rang, option.titre);
                            }}
                          >
                            <span className="chat__carte-puce"
                                  aria-hidden="true" />
                            <span className="chat__carte-corps">
                              <span className="chat__carte-titre">
                                {option.titre}
                                {option.genre && (
                                  <span className={`chat__carte-genre chat__carte-genre--${option.genre}`}>
                                    {GENRES_DOPTION[option.genre]}
                                  </span>
                                )}
                              </span>
                              {option.detail && (
                                <span className="chat__carte-detail">
                                  {option.detail}
                                </span>
                              )}
                            </span>
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        role="radio"
                        aria-checked={libres[rang]}
                        tabIndex={rangTabbable === options.length ? 0 : -1}
                        className={`chat__carte chat__carte--autre${
                          libres[rang] ? " chat__carte--choisie" : ""}`}
                        onClick={() => {
                          if (libres[rang]) return;
                          poserLibres((avant) => avant.map(
                            (l, i) => (i === rang ? true : l)));
                          poser(rang, "");
                        }}
                      >
                        <span className="chat__carte-puce" aria-hidden="true" />
                        <span className="chat__carte-corps">
                          <span className="chat__carte-titre">Autre réponse</span>
                          <span className="chat__carte-detail">
                            Votre décision, en toutes lettres — motivée si vous
                            le souhaitez.
                          </span>
                        </span>
                      </button>
                    </div>
                  )}
                  {libre && (
                    <div className="chat__libre">
                      <textarea
                        value={reponses[rang]}
                        rows={2}
                        placeholder="Votre décision…"
                        aria-label={`Réponse libre au point ${rang + 1}`}
                        onChange={(e) => poser(rang, e.target.value)}
                      />
                      <input
                        value={motifs[rang]}
                        placeholder="Motif (facultatif) — consigné au fil avec la décision"
                        aria-label={`Motif du point ${rang + 1}`}
                        onChange={(e) => poserMotifs((avant) => avant.map(
                          (m, i) => (i === rang ? e.target.value : m)))}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}

      {actif && !envoye && (
        <div className="chat__guichet-pied">
          <span className="chat__guichet-etat">
            Décisions : <strong>{tranchees} / {points.length}</strong> renseignée{tranchees > 1 ? "s" : ""}
            {tranchees > 0 && tranchees < points.length
              && " — les points sans réponse resteront ouverts, la mission les redira."}
          </span>
          <div className="chat__guichet-geste">
            <button
              type="button"
              className="bouton bouton--primaire"
              disabled={tranchees === 0 || soumis}
              onClick={() => void transmettre()}
            >
              {soumis ? "Transmission…"
                : tranchees === 0 ? "Transmettre les décisions"
                : tranchees === points.length
                  ? (points.length > 1
                      ? `Transmettre les ${points.length} décisions`
                      : "Transmettre et reprendre la mission")
                  : `Transmettre ${tranchees} décision${tranchees > 1 ? "s" : ""} sur ${points.length}`}
            </button>
            {tranchees === 0 && (
              <span className="chat__guichet-raison">
                Répondez à au moins un point pour continuer.
              </span>
            )}
          </div>
        </div>
      )}
      {actif && envoye && (
        <div className="chat__guichet-transmis" role="status">
          Décisions transmises — la mission reprend.
        </div>
      )}
    </div>
  );
}
